import React, { useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { VectorTileMap } from '@popety-io/ui'
import { API_BASE_URL } from '@/config/constants'
import { BuildingPermitTooltip } from './BuildingPermitTooltip'
import type { PermitFilters } from '@popety-io/types'

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

interface BuildingPermitsMapProps {
  filters: PermitFilters
  propertyCoordinates?: { lat: number; lon: number }
  onPermitClick?: (permitId: string) => void
  loading?: boolean
  error?: string | null
}

export const BuildingPermitsMap: React.FC<BuildingPermitsMapProps> = ({
  filters,
  propertyCoordinates,
  onPermitClick,
  loading = false,
  error = null,
}) => {
  // Build the vector tile URL with current filters
  const tileUrl = useMemo(() => {
    // Convert frontend filters to API format
    const apiFilters: any = {}

    // Map category filter
    if (filters.categories && filters.categories.length > 0) {
      apiFilters.regbl_classification_general = filters.categories.map(
        (cat) => {
          const categoryMap: Record<string, string> = {
            development: 'Construction nouvelle',
            renovation: 'Rénovation',
            transformation: 'Transformation',
            elevation: 'Surélévation',
            regularisation: 'Régularisation',
          }
          return categoryMap[cat] || cat
        }
      )
    }

    // Map status filter
    if (filters.statuses && filters.statuses.length > 0) {
      apiFilters.latest_status_db = filters.statuses.map((status) => {
        const statusMap: Record<string, string> = {
          instruction: 'En instruction',
          enregistrement: 'Enregistrement',
          approved: 'Accepté',
          pending: 'En attente',
          rejected: 'Refusé',
          completed: 'Terminé',
        }
        return statusMap[status] || status
      })
    }

    // Map date range filter
    if (filters.date_range?.start || filters.date_range?.end) {
      apiFilters.inquiry_start_date = {}
      if (filters.date_range?.start) {
        apiFilters.inquiry_start_date.min = filters.date_range.start
          .toISOString()
          .split('T')[0]
      }
      if (filters.date_range?.end) {
        apiFilters.inquiry_start_date.max = filters.date_range.end
          .toISOString()
          .split('T')[0]
      }
    }

    // Map search query
    if (filters.search_query) {
      apiFilters.query = filters.search_query
    }

    // Map postal codes
    if (filters.postalCodes && filters.postalCodes.length > 0) {
      apiFilters.postal_codes_names = filters.postalCodes
    }

    // Map cities
    if (filters.cities && filters.cities.length > 0) {
      apiFilters.municipality_name = filters.cities
    }

    // Add geospatial filtering with property coordinates
    if (filters.radius && propertyCoordinates) {
      apiFilters.radius = {
        center: {
          lat: propertyCoordinates.lat,
          lon: propertyCoordinates.lon,
        },
        distance: `${filters.radius}m`,
      }
    }

    const queryParams = {
      query: JSON.stringify({
        filters: apiFilters,
        fields: [
          'id',
          'fao_id',
          'primary_official_id',
          'secondary_official_id',
          'description',
          'city_name',
          'municipality_name',
          'postal_codes_names',
          'geo_center',
          'inquiry_start_date',
          'latest_status_db',
          'regbl_classification_general',
        ],
      }),
    }

    const queryString = new URLSearchParams(queryParams).toString()
    return `${API_BASE_URL}/building-permits/vector-tile/geo_center/{z}/{x}/{y}?${queryString}`
  }, [filters, propertyCoordinates])

  // Calculate map center
  const mapCenter = useMemo((): [number, number] => {
    if (propertyCoordinates) {
      return [propertyCoordinates.lon, propertyCoordinates.lat]
    }
    return [8.2275, 46.8182] // Switzerland center
  }, [propertyCoordinates])

  // Calculate initial zoom
  const initialZoom = useMemo(() => {
    if (propertyCoordinates) {
      // If we have property coordinates, zoom in more
      if (filters.radius) {
        // Adjust zoom based on radius
        if (filters.radius <= 500) return 15
        if (filters.radius <= 1000) return 14
        if (filters.radius <= 2000) return 13
        return 12
      }
      return 14
    }
    return 8
  }, [propertyCoordinates, filters.radius])

  // Handle feature click
  const handleFeatureClick = useCallback(
    (feature: any) => {
      const permitId = feature.properties?.id || feature.id
      if (permitId) {
        onPermitClick?.(permitId.toString())
      }
    },
    [onPermitClick]
  )

  // Handle feature hover
  const handleFeatureHover = useCallback((_feature: any) => {
    // Optional: Could implement hover effects here
  }, [])

  // Custom tooltip renderer
  const renderTooltip = useCallback((properties: any) => {
    return <BuildingPermitTooltip properties={properties} />
  }, [])

  return (
    <MapContainer>
      <VectorTileMap
        tileUrl={tileUrl}
        sourceLayer="hits"
        height="100%"
        center={mapCenter}
        zoom={initialZoom}
        pointStyle={{
          color: '#8b5cf6',
          size: 5,
          strokeColor: '#ffffff',
          strokeWidth: 1,
          hoverColor: '#7c3aed',
          selectedColor: '#6d28d9',
        }}
        labelStyle={{
          enabled: false, // Don't show labels for building permits by default
        }}
        clustering={{
          enabled: false,
        }}
        tooltip={{
          enabled: true,
          fields: [
            'id',
            'fao_id',
            'primary_official_id',
            'description',
            'city_name',
            'postal_codes_names',
            'inquiry_start_date',
            'latest_status_db',
            'regbl_classification_general',
          ],
          render: renderTooltip,
        }}
        onFeatureClick={handleFeatureClick}
        onFeatureHover={handleFeatureHover}
        loading={loading}
        error={error}
        showControls={true}
        enableMeasurement={false}
      />
    </MapContainer>
  )
}
